// Shared non-component exports; keep component modules valid Fast Refresh boundaries.
import { useCallback, useState, type ReactNode } from "react";
import { ConfirmDialog } from "./ConfirmDialog";

export type NoticeOpts = {
  title: string;
  message: ReactNode;
  confirmLabel?: string;
};

export type ConfirmOpts = {
  title: string;
  message: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  destructive?: boolean;
};

export type DialogState =
  | ({ kind: "notice"; resolve: () => void } & NoticeOpts)
  | ({ kind: "confirm"; resolve: (ok: boolean) => void } & ConfirmOpts)
  | null;

/**
 * Imperative hook around {@link ConfirmDialog} that returns promise-based
 * helpers and a React element to render. Lets us drop-in replace
 * ``window.alert`` / ``window.confirm`` call sites with minimal control
 * flow churn.
 */
export function useConfirmDialog() {
  const [state, setState] = useState<DialogState>(null);

  const notice = useCallback(
    (opts: NoticeOpts) =>
      new Promise<void>((resolve) => {
        setState({ kind: "notice", resolve, ...opts });
      }),
    [],
  );

  const confirm = useCallback(
    (opts: ConfirmOpts) =>
      new Promise<boolean>((resolve) => {
        setState({ kind: "confirm", resolve, ...opts });
      }),
    [],
  );

  const close = useCallback(() => {
    setState((current) => {
      if (!current) return null;
      if (current.kind === "notice") current.resolve();
      else current.resolve(false);
      return null;
    });
  }, []);

  const accept = useCallback(() => {
    setState((current) => {
      if (!current) return null;
      if (current.kind === "confirm") current.resolve(true);
      else current.resolve();
      return null;
    });
  }, []);

  const element = (
    <ConfirmDialog
      open={state !== null}
      variant={state?.kind ?? "notice"}
      title={state?.title ?? ""}
      message={state?.message ?? ""}
      confirmLabel={state?.confirmLabel}
      cancelLabel={state?.kind === "confirm" ? state.cancelLabel : undefined}
      destructive={state?.kind === "confirm" ? state.destructive : false}
      onConfirm={accept}
      onClose={close}
    />
  );

  return { notice, confirm, element };
}
